import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { I18nProvider } from "@/contexts/I18nContext";
import Landing from "@/pages/Landing";
import PublicRecap from "@/pages/PublicRecap";
import Legal from "@/pages/Legal";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Meetings from "@/pages/Meetings";
import MeetingDetail from "@/pages/MeetingDetail";
import Tasks from "@/pages/Tasks";
import Decisions from "@/pages/Decisions";
import Insights from "@/pages/Insights";
import Settings from "@/pages/Settings";
import MeetingJoin from "@/pages/MeetingJoin";
import ChangePassword from "@/pages/ChangePassword";
import AdminUsers from "@/pages/AdminUsers";
import Pricing from "@/pages/Pricing";
import Billing from "@/pages/Billing";
import PlatformAdminBilling from "@/pages/PlatformAdminBilling";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import VerifyEmail from "@/pages/VerifyEmail";
import EmailAction from "@/pages/EmailAction";
import Shell from "@/components/Shell";

function Boot() {
  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="w-11 h-11 rounded-2xl grid place-items-center shadow-beam" style={{ background: "hsl(var(--navy))" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 2 L20 15 H13 Z" fill="hsl(var(--cyan))" />
            <path d="M12 2 L4 15 H11 Z" fill="#ffffff" fillOpacity="0.92" />
            <path d="M9 17 H15 L13.5 22 H10.5 Z" fill="hsl(var(--gold))" />
          </svg>
        </div>
        <div className="eyebrow text-[10px] text-muted-foreground">Loading workspace…</div>
      </div>
    </div>
  );
}

// Guests landing on "/" see the marketing site; everyone else is sent to login.
function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Boot />;
  if (!user) {
    if (location.pathname === "/") return <Landing />;
    return <Navigate to="/login" replace />;
  }
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!user || user.role !== "Admin") return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/home" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/verify" element={<VerifyEmail />} />
              <Route path="/email-action" element={<EmailAction />} />
              <Route path="/join/:meetingId" element={<MeetingJoin />} />
              <Route path="/recap/:token" element={<PublicRecap />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/legal/:doc" element={<Legal />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
              <Route
                path="/"
                element={
                  <Protected>
                    <Shell />
                  </Protected>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="meetings" element={<Meetings />} />
                <Route path="meetings/:meetingId" element={<MeetingDetail />} />
                <Route path="tasks" element={<Tasks />} />
                <Route path="decisions" element={<Decisions />} />
                <Route path="insights" element={<Insights />} />
                <Route path="settings" element={<Settings />} />
                <Route path="pricing" element={<Pricing />} />
                <Route path="billing" element={<Billing />} />
                <Route path="admin/users" element={<AdminOnly><AdminUsers /></AdminOnly>} />
                <Route path="platform-admin/billing" element={<AdminOnly><PlatformAdminBilling /></AdminOnly>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </ThemeProvider>
  );
}
